import React, { FC, useContext } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import styled, { css } from 'styled-components';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import FormContext from 'src/presentation/contexts/FormContext';

type PropsType = {
  'data-aid': string;
  attr: string;
  label?: string;
  description?: string;
  [keyOption: string]: any;
};

const PhoneNumberInput: FC<PropsType> = props => {
  const { errors, workingState, updateState, resetError, onBlur: onBlurFromContext } = useContext(FormContext);

  const {
    'data-aid': dataAid,
    attr,
    defaultCountry = 'US',
    description,
    disabled,
    errorAttr = attr,
    international = true,
    label,
    onBlur,
    onChange,
    readOnly,
    value,
    ...otherProps
  } = props;
  const error = find(errors, error => get(error, 'attr') === errorAttr);
  const errorMessage = error && get(error, 'detail');

  const handleInputChange = (value: string) => {
    if (onChange) {
      onChange(value);
      return;
    }
    resetError({ attr, errors });
    updateState(set({}, attr, isNil(value) ? '' : value));
  };

  const val = isNil(value) ? get(workingState, attr) : value;

  return (
    <StyledContainer errorMessage={errorMessage}>
      {label && <StyledLabel data-aid={`${dataAid}-label`}>{label}</StyledLabel>}
      {description && <StyledDescription data-aid={`${dataAid}-description`}>{description}</StyledDescription>}
      {/* @ts-ignore */}
      <PhoneInput
        countrySelectProps={{ disabled: disabled || readOnly }}
        data-aid={dataAid}
        defaultCountry={defaultCountry}
        disabled={disabled}
        international={international}
        onBlur={onBlur || onBlurFromContext}
        onChange={handleInputChange}
        readOnly={readOnly}
        value={val}
        {...otherProps}
      />
      {errorMessage && <ErrorLabel data-aid={`${dataAid}-error`}>{errorMessage}</ErrorLabel>}
    </StyledContainer>
  );
};

const StyledLabel = styled.div`
  align-self: flex-start;
  font-weight: bolder;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

const StyledDescription = styled.div`
  align-self: flex-start;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

const getInputStyles = errorMessage => css`
  background-color: ${p => p.theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.gray300};
  flex: 1 1 auto;
  font-size: ${p => p.theme.fontSize.base};
  height: 32px;
  padding: 8px;
  transition: border-color 100ms ease-in;
  width: 100%;

  &:focus {
    border-color: ${p => p.theme.colors.gray600};
    outline: none;
  }

  &[disabled] {
    background-color: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray500};
  }

  ${errorMessage && errorStyles};
`;

const errorStyles = css`
  border-color: ${p => p.theme.colors.red400};
  &:focus {
    border-color: ${p => p.theme.colors.red600};
  }
`;

export const StyledContainer = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};

  input {
    ${({ errorMessage }) => getInputStyles(errorMessage)}
  }
`;

export const ErrorLabel = styled.div`
  color: ${p => p.theme.colors.red400};
`;

export default PhoneNumberInput;
